// @givmo/passkeys — the organization door on a sign-in page (AS-WEB30): "Continue with Organization
// SSO" on the charity console and Board's director door, "Sign in with Organization SSO" on the
// employer portal and the company console (D-AS-48 (7a), D-AS-45's addendum) — last in the ways in,
// after the personal ones (company sign-in is an addition, never a replacement), in the passkey
// button's look with the apps' building icon (frames W1, L1–L3).
//
// Drawn ONLY where it can work (D-AS-48 6): the backend's routes are served AND this console's own
// callback is on this environment's allowlist — `GET /authn/sso/availability` answers both without
// spending a sign-in attempt. Anywhere else the page draws exactly as it did before (frame W0).
// Asked once per page load and kept per tab (the passkey probe's two layers, a key per callback),
// every storage access in a `try`:
//   - `available`   30 min — it changes only with a deploy or an operator's allowlist edit;
//   - `unavailable` 10 min — a flip round reaches an open tab reasonably soon;
//   - `unknown`      1 min — a 429, a 5xx or no answer says nothing either way.

import { useEffect, useState } from 'react';
import { Button, Icon } from '@givmo/design-system';
import { Building2 } from 'lucide-react';
import { organizationSsoLabel, type OrganizationSsoVerb } from './organization-sso-copy';
import {
  readOrganizationSsoAvailability,
  type OrganizationSsoAvailability,
} from './organization-sso-transport';

/**
 * Where each console's door returns: `/sso/callback` on the page's own origin. ⚠ AN OPERATOR
 * CONTRACT: joined to the console's origin it is the exact URI each environment's
 * `EMPLOYER_SSO_ALLOWED_REDIRECT_URIS` must hold (dev's in-repo default, the backend's `dev.yml`), and
 * it matches exactly, so renaming it is an operator change in every environment.
 */
export const ORGANIZATION_SSO_CALLBACK_PATH = '/sso/callback';

/** This page's callback URI: its own origin and the one path. */
export function organizationSsoCallbackUrl(origin: string = globalThis.location.origin): string {
  return `${origin}${ORGANIZATION_SSO_CALLBACK_PATH}`;
}

const STORAGE_PREFIX = 'givmo.organization-sso-availability.v1:';

const TTL_MS: Record<OrganizationSsoAvailability, number> = {
  available: 30 * 60 * 1000,
  unavailable: 10 * 60 * 1000,
  unknown: 60 * 1000,
};

interface CachedVerdict {
  verdict: OrganizationSsoAvailability;
  expiresAt: number;
}

/** The in-flight or settled read for this page load, per callback URI. */
const probes = new Map<string, Promise<OrganizationSsoAvailability>>();

/**
 * Drop the memoised reads, and by default the stored verdicts too. A TEST SEAM: a suite that renders a
 * door must call it. `keepStoredVerdict: true` drops only the per-page-load memo, as a reload does.
 */
export function resetOrganizationSsoAvailabilityProbe(
  options: { keepStoredVerdict?: boolean } = {},
): void {
  probes.clear();
  if (options.keepStoredVerdict === true) return;
  try {
    const store = globalThis.sessionStorage;
    if (store === undefined) return;
    for (const key of Object.keys(store)) {
      if (key.startsWith(STORAGE_PREFIX)) store.removeItem(key);
    }
  } catch {
    // Storage is a convenience here, never a requirement.
  }
}

function readCachedVerdict(callbackUrl: string, now: number): OrganizationSsoAvailability | null {
  try {
    const raw = globalThis.sessionStorage?.getItem(STORAGE_PREFIX + callbackUrl);
    if (typeof raw !== 'string') return null;
    const parsed = JSON.parse(raw) as Partial<CachedVerdict>;
    if (typeof parsed.verdict !== 'string' || typeof parsed.expiresAt !== 'number') return null;
    if (!(parsed.verdict in TTL_MS)) return null;
    return parsed.expiresAt > now ? (parsed.verdict as OrganizationSsoAvailability) : null;
  } catch {
    return null;
  }
}

function writeCachedVerdict(
  callbackUrl: string,
  verdict: OrganizationSsoAvailability,
  now: number,
): void {
  try {
    const entry: CachedVerdict = { verdict, expiresAt: now + TTL_MS[verdict] };
    globalThis.sessionStorage?.setItem(STORAGE_PREFIX + callbackUrl, JSON.stringify(entry));
  } catch {
    // Full quota or a blocked store — the in-memory memo still bounds this page load.
  }
}

async function resolveVerdict(callbackUrl: string): Promise<OrganizationSsoAvailability> {
  const cached = readCachedVerdict(callbackUrl, Date.now());
  if (cached !== null) return cached;
  const verdict = await readOrganizationSsoAvailability(callbackUrl);
  writeCachedVerdict(callbackUrl, verdict, Date.now());
  return verdict;
}

/**
 * Whether this page can run the organization door: `true` once the backend says so for this
 * console's callback, `false` until then and on any doubt. Starts `false` and only ever flips to
 * `true`, so a page where the door cannot work never draws it.
 */
export function useOrganizationSsoAvailability(
  callbackUrl: string = organizationSsoCallbackUrl(),
): boolean {
  const [available, setAvailable] = useState(false);
  useEffect(() => {
    let subscribed = true;
    let probe = probes.get(callbackUrl);
    if (probe === undefined) {
      probe = resolveVerdict(callbackUrl);
      probes.set(callbackUrl, probe);
    }
    void probe.then((verdict) => {
      if (subscribed) setAvailable(verdict === 'available');
    });
    return () => {
      subscribed = false;
    };
  }, [callbackUrl]);
  return available;
}

export interface OrganizationSsoButtonProps {
  /** The page's own verb: "Continue with …" or "Sign in with …". */
  verb: OrganizationSsoVerb;
  /** Opens the door's step (the work email). */
  onOpen: () => void;
  disabled?: boolean;
  /** Whether this page can run the door (`useOrganizationSsoAvailability`), read once by its host. */
  available: boolean;
}

/** The door's way in, last in the ways in, and nothing at all where it cannot work. */
export function OrganizationSsoButton({
  verb,
  onOpen,
  disabled = false,
  available,
}: OrganizationSsoButtonProps) {
  if (!available) return null;
  return (
    <Button
      appearance="saas"
      tone="secondary"
      size="lg"
      fullWidth
      disabled={disabled}
      leadingIcon={<Icon icon={Building2} size={18} aria-hidden />}
      className="justify-center gap-10 font-w600"
      onClick={onOpen}
      testId="organization-sso-door"
    >
      {organizationSsoLabel(verb)}
    </Button>
  );
}
